// HoverZoomImg — an <img> that shows a larger version of itself on hover, in a
// PORTAL on document.body (the same escape-the-cell's-overflow:hidden trick
// CascadeSelect's popover and FormulaCell's help popover already use — a table
// cell clips anything scaled up in place, so a bigger version has to live
// outside it). Shared by ImageCell and ThumbCell — one picture-hover behavior,
// not two (Alex, 2026-08-27: "is there a way of the image be bigger on
// hover"). A short close delay (matching FormulaCell's own) means crossing the
// small gap between the thumbnail and the preview doesn't flicker it shut.
//
//   src   — the image url (same url the small version already uses — this
//           just displays it larger via CSS, it does not fetch a hi-res copy)
//   title — optional tooltip on the small image
//   style — the small image's own style (width/height/border/etc.)
//
// The preview takes the picture's OWN shape (Alex, 2026-10-20, on logos: "adjust the
// hover window shape to match the logo"): the longer side is HOVER_PREVIEW_SIZE (a
// wide logo gets a wide window, not a square with empty bars). Until the natural
// size is known it falls back to the square.

import { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

const HOVER_PREVIEW_SIZE = 180;

function previewBox(n) {
  if (!n || !n.w || !n.h) return { width: HOVER_PREVIEW_SIZE, height: HOVER_PREVIEW_SIZE };
  const k = HOVER_PREVIEW_SIZE / Math.max(n.w, n.h);
  return { width: Math.round(n.w * k), height: Math.round(n.h * k) };
}

export default function HoverZoomImg({ src, title, style }) {
  const [previewPos, setPreviewPos] = useState(null);
  const [natural, setNatural] = useState(null);
  const imgRef = useRef(null);
  const closeTimerRef = useRef(null);

  const openPreview = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    const rect = imgRef.current?.getBoundingClientRect();
    if (rect) setPreviewPos({ left: rect.right + 6, top: rect.top + rect.height / 2 });
  }, []);
  const scheduleClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setPreviewPos(null), 150);
  }, []);
  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt=""
        title={title}
        loading="lazy"
        onLoad={e => setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
        onMouseEnter={openPreview}
        onMouseLeave={scheduleClose}
        style={{ ...style, cursor: 'zoom-in' }}
      />
      {previewPos && createPortal(
        <div
          onMouseEnter={openPreview}
          onMouseLeave={scheduleClose}
          style={{
            position: 'fixed', left: previewPos.left, top: previewPos.top,
            transform: 'translateY(-50%)', zIndex: 400,
            background: 'var(--th-row-even)', border: '1px solid var(--th-border-subtle)',
            borderRadius: 4, padding: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
          }}
        >
          <img
            src={src} alt=""
            style={{ ...previewBox(natural), objectFit: 'contain', display: 'block' }}
          />
        </div>,
        document.body
      )}
    </>
  );
}
